import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { seoData } from '@/utils/seoData';

const ContactPage = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const staticContent = {
    tr: {
      title: 'İletişim',
      subtitle: 'Projeleriniz hakkında konuşalım',
      form: {
        name: 'Adınız Soyadınız',
        email: 'E-posta Adresiniz',
        phone: 'Telefon Numaranız',
        subject: 'Konu',
        message: 'Mesajınız',
        submit: 'Mesaj Gönder',
        success: 'Mesajınız başarıyla gönderildi!',
        error: 'Bir hata oluştu. Lütfen tekrar deneyin.',
      },
      info: {
        title: 'İletişim Bilgileri',
      },
    },
    en: {
      title: 'Contact',
      subtitle: 'Let\'s talk about your projects',
      form: {
        name: 'Your Name',
        email: 'Your Email',
        phone: 'Your Phone',
        subject: 'Subject',
        message: 'Your Message',
        submit: 'Send Message',
        success: 'Your message has been sent successfully!',
        error: 'An error occurred. Please try again.',
      },
      info: {
        title: 'Contact Information',
      },
    },
  };

  const t = staticContent[language];
  const contactInfo = siteSettings?.contact_info?.[language] || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t.form.success);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const pageSeo = seoData.contact[language];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="contact-title"
          >
            {t.title}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600" data-testid="contact-subtitle">{t.subtitle}</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Input
                    name="name"
                    placeholder={t.form.name}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder={t.form.email}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <Input
                    name="phone"
                    placeholder={t.form.phone}
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <Input
                    name="subject"
                    placeholder={t.form.subject}
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    data-testid="contact-subject"
                  />
                </div>
                <div>
                  <Textarea
                    name="message"
                    placeholder={t.form.message}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? '...' : t.form.submit}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t.info.title}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{language === 'tr' ? 'Adres' : 'Address'}</h3>
                    <p className="text-gray-600">{contactInfo.address || 'Fatih Mah, 1197. Sk. No:2/1, Sarnıç, Gaziemir/İzmir'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{language === 'tr' ? 'Telefon' : 'Phone'}</h3>
                    <p className="text-gray-600">{contactInfo.phone || '+90 232 433 1 435'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{language === 'tr' ? 'E-posta' : 'Email'}</h3>
                    <p className="text-gray-600">{contactInfo.email || 'info@enerotomasyon.com.tr'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;